/**
 * DiagnosisListsPage.tsx — Owner-only admin page for diagnosis config lists.
 *
 * Two managed lists used across the diagnosis loop (line-item drawer, DVI):
 *   - Urgency Levels (label + color), shown as colored tags on findings.
 *   - Photo Captions (text), quick-pick captions attached to diagnosis PHOTOS.
 *
 * WP-07 NOTE — these are still photo captions, and that is now the narrow thing
 * they are. Quick values for an INSPECTION ITEM (with a per-item configurable
 * label, a unit, and a severity) live in Settings > Inspection Note Presets
 * (InspectionNotePresetsPage). This list is retained because its rows were
 * copied forward into that system by migration 169, not superseded in place.
 *
 * Reorder is implemented with up/down arrow buttons that swap adjacent items and
 * PUT the full id list to the /reorder endpoint (no drag-and-drop dependency).
 *
 * Route: /owner/settings/diagnosis-lists  (owner-only; mounted under owner routes)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Card,
  Table,
  Button,
  Space,
  Input,
  Switch,
  ColorPicker,
  Popconfirm,
  App,
  Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Color } from 'antd/es/color-picker';
import {
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  PlusOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import {
  getUrgencyLevels,
  createUrgencyLevel,
  updateUrgencyLevel,
  deleteUrgencyLevel,
  reorderUrgencyLevels,
  getPhotoCaptions,
  createPhotoCaption,
  updatePhotoCaption,
  deletePhotoCaption,
  reorderPhotoCaptions,
  UrgencyLevel,
  PhotoCaption,
} from '../../services/diagnosisListsApi';

const { Title, Paragraph } = Typography;

const DiagnosisListsPageInner: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();

  const rolePrefix = location.pathname.startsWith('/owner') ? '/owner' : '/admin';

  const [loading, setLoading] = useState(true);
  const [levels, setLevels] = useState<UrgencyLevel[]>([]);
  const [captions, setCaptions] = useState<PhotoCaption[]>([]);

  // Add-row state
  const [newLevelLabel, setNewLevelLabel] = useState('');
  const [newLevelColor, setNewLevelColor] = useState('#faad14');
  const [newCaptionText, setNewCaptionText] = useState('');

  // Per-row saving guards
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lv, cp] = await Promise.all([getUrgencyLevels(), getPhotoCaptions()]);
      setLevels(lv);
      setCaptions(cp);
    } catch (err) {
      message.error('Failed to load diagnosis lists');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Urgency Level handlers ──────────────────────────────────────────────

  const handleAddLevel = async () => {
    const label = newLevelLabel.trim();
    if (!label) {
      message.warning('Enter a label');
      return;
    }
    setBusy(true);
    try {
      await createUrgencyLevel({ label, color: newLevelColor, sort_order: levels.length });
      setNewLevelLabel('');
      setNewLevelColor('#faad14');
      message.success('Urgency level added');
      await load();
    } catch {
      message.error('Failed to add urgency level');
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateLevel = async (
    id: number,
    patch: { label?: string; color?: string; is_active?: boolean },
  ) => {
    setBusy(true);
    try {
      await updateUrgencyLevel(id, patch);
      message.success('Saved');
      await load();
    } catch {
      message.error('Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteLevel = async (id: number) => {
    setBusy(true);
    try {
      await deleteUrgencyLevel(id);
      message.success('Deleted');
      await load();
    } catch {
      message.error('Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  const moveLevel = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= levels.length) return;
    const ids = levels.map((l) => l.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setBusy(true);
    try {
      const next = await reorderUrgencyLevels(ids);
      setLevels(next);
    } catch {
      message.error('Failed to reorder');
    } finally {
      setBusy(false);
    }
  };

  // ── Photo Caption handlers ──────────────────────────────────────────────

  const handleAddCaption = async () => {
    const text = newCaptionText.trim();
    if (!text) {
      message.warning('Enter caption text');
      return;
    }
    setBusy(true);
    try {
      await createPhotoCaption({ text, sort_order: captions.length });
      setNewCaptionText('');
      message.success('Caption added');
      await load();
    } catch {
      message.error('Failed to add caption');
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateCaption = async (
    id: number,
    patch: { text?: string; is_active?: boolean },
  ) => {
    setBusy(true);
    try {
      await updatePhotoCaption(id, patch);
      message.success('Saved');
      await load();
    } catch {
      message.error('Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteCaption = async (id: number) => {
    setBusy(true);
    try {
      await deletePhotoCaption(id);
      message.success('Deleted');
      await load();
    } catch {
      message.error('Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  const moveCaption = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= captions.length) return;
    const ids = captions.map((c) => c.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setBusy(true);
    try {
      const next = await reorderPhotoCaptions(ids);
      setCaptions(next);
    } catch {
      message.error('Failed to reorder');
    } finally {
      setBusy(false);
    }
  };

  // ── Columns ─────────────────────────────────────────────────────────────

  const reorderCell = (
    index: number,
    total: number,
    move: (i: number, d: -1 | 1) => void,
  ) => (
    <Space>
      <Button
        size="small"
        icon={<ArrowUpOutlined />}
        disabled={index === 0 || busy}
        onClick={() => move(index, -1)}
        aria-label="Move up"
      />
      <Button
        size="small"
        icon={<ArrowDownOutlined />}
        disabled={index === total - 1 || busy}
        onClick={() => move(index, 1)}
        aria-label="Move down"
      />
    </Space>
  );

  const levelColumns: ColumnsType<UrgencyLevel> = [
    {
      title: 'Order',
      key: 'order',
      width: 110,
      render: (_v, _r, index) => reorderCell(index, levels.length, moveLevel),
    },
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      render: (label: string, record) => (
        <Input
          defaultValue={label}
          disabled={busy}
          onBlur={(e) => {
            const next = e.target.value.trim();
            if (next && next !== record.label) handleUpdateLevel(record.id, { label: next });
          }}
        />
      ),
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      width: 120,
      render: (color: string, record) => (
        <ColorPicker
          value={color}
          disabled={busy}
          onChangeComplete={(c: Color) => {
            const hex = c.toHexString();
            if (hex !== record.color) handleUpdateLevel(record.id, { color: hex });
          }}
        />
      ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (active: boolean, record) => (
        <Switch
          checked={active}
          disabled={busy}
          onChange={(checked) => handleUpdateLevel(record.id, { is_active: checked })}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 70,
      render: (_v, record) => (
        <Popconfirm
          title="Delete this urgency level?"
          onConfirm={() => handleDeleteLevel(record.id)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <Button size="small" danger icon={<DeleteOutlined />} disabled={busy} />
        </Popconfirm>
      ),
    },
  ];

  const captionColumns: ColumnsType<PhotoCaption> = [
    {
      title: 'Order',
      key: 'order',
      width: 110,
      render: (_v, _r, index) => reorderCell(index, captions.length, moveCaption),
    },
    {
      title: 'Text',
      dataIndex: 'text',
      key: 'text',
      render: (text: string, record) => (
        <Input
          defaultValue={text}
          disabled={busy}
          onBlur={(e) => {
            const next = e.target.value.trim();
            if (next && next !== record.text) handleUpdateCaption(record.id, { text: next });
          }}
        />
      ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (active: boolean, record) => (
        <Switch
          checked={active}
          disabled={busy}
          onChange={(checked) => handleUpdateCaption(record.id, { is_active: checked })}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 70,
      render: (_v, record) => (
        <Popconfirm
          title="Delete this caption?"
          onConfirm={() => handleDeleteCaption(record.id)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <Button size="small" danger icon={<DeleteOutlined />} disabled={busy} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`${rolePrefix}/settings`)}
          style={{ paddingLeft: 0 }}
        >
          Back to Settings
        </Button>
        <Title level={3} style={{ marginTop: 8 }}>
          <Space>
            <ExperimentOutlined />
            Diagnosis Lists
          </Space>
        </Title>
        <Paragraph type="secondary">
          Configure urgency levels and photo captions used in diagnosis reports.
        </Paragraph>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card
            title="Urgency Levels"
            extra={null}
            styles={{ body: { paddingTop: 12 } }}
          >
            <Table
              rowKey="id"
              size="small"
              columns={levelColumns}
              dataSource={levels}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
            <div style={{ marginTop: 16 }}>
              <Space wrap>
                <Input
                  placeholder="Label (e.g. Critical)"
                  value={newLevelLabel}
                  onChange={(e) => setNewLevelLabel(e.target.value)}
                  onPressEnter={handleAddLevel}
                  style={{ width: 200 }}
                  disabled={busy}
                />
                <ColorPicker
                  value={newLevelColor}
                  disabled={busy}
                  onChangeComplete={(c: Color) => setNewLevelColor(c.toHexString())}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddLevel}
                  loading={busy}
                >
                  Add Level
                </Button>
              </Space>
            </div>
          </Card>

          <Card title="Photo Captions" styles={{ body: { paddingTop: 12 } }}>
            <Table
              rowKey="id"
              size="small"
              columns={captionColumns}
              dataSource={captions}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
            <div style={{ marginTop: 16 }}>
              <Space wrap>
                <Input
                  placeholder="Caption text (e.g. Worn brake pad)"
                  value={newCaptionText}
                  onChange={(e) => setNewCaptionText(e.target.value)}
                  onPressEnter={handleAddCaption}
                  style={{ width: 260 }}
                  disabled={busy}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddCaption}
                  loading={busy}
                >
                  Add Caption
                </Button>
              </Space>
            </div>
          </Card>
        </Space>
      )}
    </div>
  );
};

const DiagnosisListsPage: React.FC = () => (
  <App>
    <DiagnosisListsPageInner />
  </App>
);

export default DiagnosisListsPage;
